import React, { useState, useRef, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  makeStyles,
  useTheme,
} from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import SearchInput from "./filters/SearchInput";
import TodayIcon from "@material-ui/icons/Today";
import ArrowBack from "@material-ui/icons/ArrowBack";
import Button from "@civicplus/preamble-ui/lib/Button";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import TuneRoundedIcon from "@material-ui/icons/TuneRounded";
import { AppDispatch, RootState } from "../../app/store";
import CategoriesCombo from "./filters/CategoriesCombo";
import Dialog from "@civicplus/preamble-ui/lib/Dialog";
import Typography from "@civicplus/preamble-ui/lib/Typography";
import Announcement from "../../components/Announcement";
import { setScrollToToday, updateEventFilters } from "../../actions";
import DateFilter from "./filters/DateFilter";
import { SnackbarKey, useSnackbar } from "notistack";
import Badge from "@material-ui/core/Badge";
import classNames from "classnames";
import { snackbarContainerId } from "../../App";
import { makeFullUrl } from "../../utils";
import { scroller } from "react-scroll";
import { lighten } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.between("sm", "md")]: {
      flexDirection: "column",
    },
    padding: process.env.REACT_APP_EMBED ? theme.spacing(2) : undefined,
  },
  dialogContent: {
    padding: theme.spacing(1, 3, 3, 3),
  },
  drawerHeaderWrapper: {
    justifyContent: "space-between",
    [theme.breakpoints.down("xs")]: {
      flexWrap: "nowrap",
    },
  },
  drawerHeader: {
    padding: theme.spacing(2, 2, 0, 2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 2, 2, 2),
    },
  },
  drawer: {
    padding: theme.spacing(2),
  },
  dateFilter: {
    [theme.breakpoints.up("lg")]: {
      flexDirection: "column",
    },
    "& .prmbl-range-component": {
      padding: 0,
      paddingRight: theme.spacing(2),
      [theme.breakpoints.up("lg")]: {
        marginBottom: theme.spacing(2),
        paddingRight: 0,
        flexBasis: "100%",
        maxWidth: "100%",
      },
    },
  },
  autocomplete: {
    "& #categoriesAutocomplete": {
      minHeight: "3.5rem",
    },
  },
  break: {
    flexBasis: "100%",
    height: "0",
  },
  badgeSpacing: {
    marginTop: theme.spacing(0.5),
  },
  badge: {
    "& .MuiBadge-badge": {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main,
    },
  },
  inputBorderColor: {
    "& fieldset": {
      borderColor: lighten(theme.palette.grey["600"], 0.2),
    },
  },
  searchInput: {
    "& .MuiBadge-badge": {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

interface EventFiltersProps {
  isEventList?: boolean;
}

const EventFilters: React.FC<EventFiltersProps> = ({ isEventList = true }) => {
  const eventFilters = useSelector((state: RootState) => state.eventFilters);
  const eventList = useSelector((state: RootState) => state.eventsList);
  const portalCustomization = useSelector(
    (state: RootState) => state.portalCustomization
  );
  const dispatch = useDispatch<AppDispatch>();
  const classes = useStyles();
  const theme = useTheme();
  const firstRender = useRef(true);
  const history = useHistory();
  const oldFilters = useRef(eventFilters);
  const [openFilters, setOpenFilters] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(true);
  const [filtersActive, setFiltersActive] = useState(0);
  const [showTodayDialog, setTodayDialog] = useState(false);

  const isMobileView = useMediaQuery(theme.breakpoints.down("xs"));
  const isTabletView = useMediaQuery(theme.breakpoints.between("xs", "lg"));
  const isDesktopView = useMediaQuery(theme.breakpoints.up("lg"));

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (eventFilters) {
      const fromAndTo = [
        eventFilters.date?.to,
        eventFilters.date?.from,
        eventFilters.categories?.length,
      ];
      setFiltersActive(fromAndTo.filter(e => e !== null && e !== 0).length);
    }
  }, [eventFilters]);

  useEffect(() => {
    let snackbarId: SnackbarKey | undefined;
    if (
      oldFilters.current.categories !== eventFilters.categories ||
      oldFilters.current.date !== eventFilters.date ||
      oldFilters.current.contentTypes?.length !==
        eventFilters.contentTypes?.length
    ) {
      oldFilters.current = eventFilters;
      snackbarId = enqueueSnackbar("Filter Updated", {
        //Keeping preamble standards when we want snackbar to be themed with client provided color
        variant: "info",
      });
    }

    return () => {
      closeSnackbar(snackbarId);
    };
  }, [closeSnackbar, enqueueSnackbar, eventFilters]);

  const handleViewTodayClick = () => {
    const today = new Date();
    // check if today is in the eventList array
    const todayInList = eventList?.find(event => {
      const eventDate = new Date(event.startDateTime);
      return eventDate.toDateString() === today.toDateString();
    });
    if (todayInList) {
      // scroll to today Events
      const li = todayInList.id.toString();
      scroller.scrollTo(li, {
        containerId: "scroll-wrap",
        offset: -50,
      });
    } else {
      dispatch(setScrollToToday(true));
      // Update events filters state. This will trigger the initial load of events again.
      if (
        !eventFilters.date ||
        (!eventFilters.date.from && !eventFilters.date.to)
      ) {
        dispatch(
          updateEventFilters({
            calendarDate: undefined,
          })
        );
      } else {
        // Since we are deleting the date range, this will render the toast with the filters update message.
        dispatch(
          updateEventFilters({
            calendarDate: undefined,
            date: undefined,
          })
        );
      }
    }
  };

  const handleCleanRangeDate = () => {
    const today = new Date();
    dispatch(
      updateEventFilters({
        date: undefined,
        skip: 0,
        calendarDate: today.toDateString(),
      })
    );
    handleCloseTodayDialog();
  };

  const onSearch = useCallback(
    (search: string, contentTypes?: string[]) => {
      const contentTypesString =
        contentTypes && contentTypes.length > 0
          ? "&filters=" + encodeURIComponent(contentTypes.join(","))
          : "";

      dispatch(updateEventFilters({ search, contentTypes }));

      if (search || (contentTypes && contentTypes.length > 0)) {
        if (process.env.REACT_APP_EMBED) {
          // For embedded portals, we need to open a new window and search the main portal site.
          let categoryFilter = "";
          if (
            oldFilters.current.categories &&
            oldFilters.current.categories.length > 0
          ) {
            categoryFilter = `&category_id=${oldFilters.current.categories.join()}`;
          }
          window.open(
            makeFullUrl(
              `/search?q=${search}${contentTypesString}${categoryFilter}`
            )
          );
          return;
        } else {
          // populate the search query url
          if (history) {
            const params = new URLSearchParams(history.location.search);
            if (
              oldFilters.current.categories &&
              oldFilters.current.categories.length > 0
            ) {
              params.set("category_id", oldFilters.current.categories.join());
            } else {
              params.delete("category_id");
            }

            if (contentTypes && contentTypes.length > 0) {
              params.set("filters", contentTypes.join());
            } else {
              params.delete("filters");
            }

            if (search || (contentTypes && contentTypes.length > 0)) {
              params.set("q", search);
            } else {
              params.delete("q");
            }

            let searchUrl = `?${decodeURIComponent(params.toString())}`;
            if (search || (contentTypes && contentTypes.length > 0)) {
              searchUrl = `/search?${decodeURIComponent(params.toString())}`;
            }

            history.push(searchUrl);
          }
        }
      } else {
        history.push("/");
      }
      firstRender.current = true;
    },
    [dispatch, history]
  );

  const handleCloseTodayDialog = () => {
    setTodayDialog(false);
  };
  const handleOpenFilters = () => {
    const sidebar = document.getElementById("clerk-embed-sidebar");
    const simple = document.getElementById("clerk-embed-simple");

    const isEmbedded = !!process.env.REACT_APP_EMBED;
    const hideDateFilterForEmbed = !sidebar && !simple;

    const shouldShowDateFilter =
      !isEmbedded || (isEmbedded && !hideDateFilterForEmbed);
    setShowDateFilter(shouldShowDateFilter);
    setOpenFilters(true);
  };

  const popoverTitle = "Select your date range and/or categories";
  const filtersInputs = (
    <>
      {showDateFilter && <DateFilter />}
      <CategoriesCombo />
    </>
  );
  const handleClose = () => {
    setOpenFilters(false);
  };

  // Workaround to allow snackbar to be visible from a Dialog
  useEffect(() => {
    const node = document.getElementById(snackbarContainerId);
    if (openFilters && node && (isTabletView || isMobileView)) {
      node.setAttribute("aria-hidden", "false");
    }
  }, [openFilters, isTabletView, isMobileView]);

  const goBackToEventsTable = () => {
    dispatch(
      updateEventFilters({
        ...eventFilters,
        search: "",
        contentTypes: undefined,
      })
    );
    history.push("/");
  };

  const RenderFilters = (
    <>
      <Dialog
        open={openFilters && (isTabletView || isMobileView)}
        onClose={handleClose}
        title={popoverTitle}
        sx={{
          dialogContent: classes.dialogContent,
        }}
        fullWidth={true}
        maxWidth="sm"
      >
        {filtersInputs}
      </Dialog>
    </>
  );

  return (
    <div className={classes.root}>
      <section>
        <Announcement />
      </section>
      <Grid
        container={true}
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        component="section"
        role="toolbar"
        aria-label="Filters, Search, and Jump to Today button"
      >
        <Grid xs={12}>
          <Grid container={true} spacing={2}>
            {!isTabletView && !isMobileView && (
              <Grid xs={12} sm={6}>
                <div className={classes.autocomplete}>
                  <CategoriesCombo />
                </div>
              </Grid>
            )}
            <Grid xs={12} md={12} lg={6} data-testid="searchContainer">
              {!portalCustomization?.hidePublicPortalSearch && (
                <SearchInput
                  onSearch={onSearch}
                  data-testid="searchInput"
                  label="Search"
                  placeholder="Search all content..."
                  InputLabelProps={{
                    shrink: true,
                  }}
                  className={classes.searchInput}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        {!isTabletView && (
          <div aria-hidden={true} className={classes.break}></div>
        )}
        {isEventList ? (
          <Grid>
            <Button
              startIcon={<TodayIcon />}
              onClick={handleViewTodayClick}
              disabled={!eventList}
              className={classNames({ [classes.badgeSpacing]: isTabletView })}
              aria-label="View Today"
            >
              View Today
            </Button>
          </Grid>
        ) : (
          <Grid data-testid="goBackBtn">
            <Button startIcon={<ArrowBack />} onClick={goBackToEventsTable}>
              Back
            </Button>
          </Grid>
        )}
        <Grid>
          {!isDesktopView && (
            <Badge
              overlap="rectangular"
              badgeContent={filtersActive}
              className={classNames(classes.badge, {
                [classes.badgeSpacing]: isTabletView,
              })}
            >
              <Button
                startIcon={<TuneRoundedIcon />}
                onClick={handleOpenFilters}
                aria-label={`${filtersActive} active filter${
                  filtersActive === 1 ? "" : "s"
                }`}
                title="Filtering by date range or categories"
              >
                Filters
              </Button>
            </Badge>
          )}
        </Grid>
      </Grid>
      <Dialog
        title="Confirm clear date range"
        actions={[
          <Button key="clear" color="primary" onClick={handleCleanRangeDate}>
            Clear
          </Button>,
          <Button key="clear-cancel" onClick={handleCloseTodayDialog}>
            Cancel
          </Button>,
        ]}
        open={showTodayDialog}
        onClose={handleCloseTodayDialog}
      >
        <Typography variant="body1">
          Today’s date is out of the selected range, would you like to clear
          your date range filter?
        </Typography>
      </Dialog>
      {RenderFilters}
    </div>
  );
};

export default EventFilters;
